/**
 * Recent Transactions Table Component
 * Displays recent payment transactions
 */

import React from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useColorModeValue,
  Text,
  Box,
} from "@chakra-ui/react";

interface Transaction {
  id: string;
  date: string;
  user: string;
  amount: number;
  method: string;
  status: "success" | "failed" | "pending" | "refunded";
  type: "subscription" | "one-time" | "refund";
}

const RecentTransactionsTable: React.FC = () => {
  const textColor = useColorModeValue("#575757", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const rowHoverBg = useColorModeValue("gray.100", "gray.700");
  const secondaryTextColor = useColorModeValue("gray.400", "white");
  const cardBg = useColorModeValue("white", "#151517");

  // Mock transaction data
  const transactions: Transaction[] = [
    {
      id: "TXN-001",
      date: "2024-12-15",
      user: "john.doe@example.com",
      amount: 50.00,
      method: "Credit Card",
      status: "success",
      type: "subscription",
    },
    {
      id: "TXN-002",
      date: "2024-12-15",
      user: "jane.smith@example.com",
      amount: 50.00,
      method: "PayPal",
      status: "success",
      type: "subscription",
    },
    {
      id: "TXN-003",
      date: "2024-12-14",
      user: "bob.wilson@example.com",
      amount: 49.99,
      method: "Credit Card",
      status: "failed",
      type: "one-time",
    },
    {
      id: "TXN-004",
      date: "2024-12-14",
      user: "alice.brown@example.com",
      amount: 50.00,
      method: "Stripe",
      status: "success",
      type: "subscription",
    },
    {
      id: "TXN-005",
      date: "2024-12-13",
      user: "charlie.davis@example.com",
      amount: 99.99,
      method: "Credit Card",
      status: "refunded",
      type: "refund",
    },
    {
      id: "TXN-006",
      date: "2024-12-13",
      user: "diana.miller@example.com",
      amount: 50.00,
      method: "Bank Transfer",
      status: "pending",
      type: "subscription",
    },
    {
      id: "TXN-007",
      date: "2024-12-12",
      user: "edward.taylor@example.com",
      amount: 50.00,
      method: "Credit Card",
      status: "success",
      type: "subscription",
    },
    {
      id: "TXN-008",
      date: "2024-12-12",
      user: "fiona.anderson@example.com",
      amount: 50.00,
      method: "PayPal",
      status: "success",
      type: "subscription",
    },
  ];

  const getStatusBadgeProps = (status: string) => {
    switch (status) {
      case "success":
        return {
          bgGradient: "linear-gradient(135deg, #06E363, #AAFAB1)",
          color: "white",
        };
      case "failed":
        return {
          bgGradient: "linear-gradient(135deg, #E00101, #FE4244)",
          color: "white",
        };
      case "pending":
        return {
          bgGradient: "linear-gradient(135deg, #F45B0B, #F49113)",
          color: "white",
        };
      case "refunded":
        return {
          bgGradient: "linear-gradient(135deg, #6B7280, #9CA3AF)",
          color: "white",
        };
      default:
        return {
          bgGradient: "linear-gradient(135deg, #6B7280, #9CA3AF)",
          color: "white",
        };
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };


  const scrollbarThumbBg = useColorModeValue("gray.300", "gray.600");
  const scrollbarThumbHoverBg = useColorModeValue("gray.400", "gray.500");

  return (
    <Box
      overflowX="auto"
      overflowY="hidden"
      w="100%"
      sx={{
        "&::-webkit-scrollbar": {
          height: "4px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: scrollbarThumbBg,
          borderRadius: "2px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: scrollbarThumbHoverBg,
        },
      }}
    >
      <Table variant="simple" color={textColor} minW="600px">
        <Thead bg={cardBg} borderTopLeftRadius="12px" borderTopRightRadius="12px">
          <Tr>
            <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" borderTopLeftRadius="12px">
              Transaction ID
            </Th>
            <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
              Date
            </Th>
            <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
              User
            </Th>
            <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" isNumeric>
              Amount
            </Th>
            <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
              Method
            </Th>
            <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
              Type
            </Th>
            <Th color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" borderTopRightRadius="12px">
              Status
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {transactions.map((transaction) => (
            <Tr
              key={transaction.id}
              _hover={{ bg: rowHoverBg }}
              transition="background-color 0.2s"
            >
              <Td>
                <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                  {transaction.id}
                </Text>
              </Td>
              <Td>
                <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                  {formatDate(transaction.date)}
                </Text>
              </Td>
              <Td>
                <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                  {transaction.user.toLowerCase()}
                </Text>
              </Td>
              <Td isNumeric>
                <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                  ${transaction.amount.toFixed(2)}
                </Text>
              </Td>
              <Td>
                <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" textTransform="capitalize">
                  {transaction.method}
                </Text>
              </Td>
              <Td>
                <Text fontSize="sm" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" textTransform="capitalize">
                  {transaction.type.replace("-", " ")}
                </Text>
              </Td>
              <Td>
                <Badge
                  {...getStatusBadgeProps(transaction.status)}
                  fontSize="xs"
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  textTransform="uppercase"
                  p="3px 10px"
                  borderRadius="8px"
                >
                  {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                </Badge>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

export default RecentTransactionsTable;

